/**
 * TeamDrawer — slide-out panel showing online team members.
 * Fetches from GET /api/kpi/live. Open to all authenticated users; the
 * server redacts supervisor-only fields (per-rep contact count, force-change
 * attribution) for callers without `dashboard.view_team`, and the matching
 * cells are hidden in the UI as a second line of defense.
 *
 * Supervisors with `admin.rep_status.force_change` get a per-rep Override
 * button that opens a modal exposing two actions:
 *   - Status picker: same set of sub-states the rep sees in their own
 *     Header dropdown (Ready, Break, Lunch, Meeting, Personal, Training,
 *     Special Projects, Supervisor Help, PC/IT Issues). Internal state /
 *     reason_code mapping is derived server-side; supervisors never see
 *     ready/not_ready vocabulary.
 *   - Clock Out: ends the rep's session, releases their in-progress
 *     queue tasks, and pushes off-duty to apex-voice. Supervisor
 *     attribution rides on the WS event so the rep's browser toasts.
 *
 * System sub-states (`on_call`, `wrap_up`, `talking`) are tied to live
 * voice events and are not forceable — clock the rep out instead.
 */

import { useState, useEffect, useCallback } from 'react';
import { X, RefreshCw, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { apiGet, apiPost } from '../../hooks/useApi';
import { useWebSocket } from '../../providers/WebSocketProvider';
import { subscribeToKpiLiveEvents } from '../../lib/kpi-live-subscribe';
import { hasPermission, PERMISSIONS } from '../../../lib/permissions';

interface Rep {
  rep_id: string;
  name: string;
  email: string;
  current_status: string;
  /** Category of the current status reason from /api/kpi/live. Colors the
   *  presence dot for tenant-defined reasons statusColor has no case for. */
  status_category?: 'productive' | 'away' | null;
  /** Epoch-ms when the rep entered their current status. Powers the live
   *  time-in-status tick — status_duration_minutes from the server is a
   *  fallback for pre-status-tracking rows. */
  status_changed_at?: number;
  status_duration_minutes: number;
  session_duration_minutes: number;
  contacts_this_session: number;
  /**
   * #496 supervisor force-change attribution. Populated on the server
   * side of /api/kpi/live when another user set this rep's state. Drives
   * the "forced by X" badge on the row.
   */
  forced_by_id?: string | null;
  forced_by_name?: string | null;
  forced_note?: string | null;
}

interface StatusReason {
  code: string;
  label: string;
  category: 'productive' | 'away' | null;
  sort_order: number;
}

interface TeamDrawerProps {
  open: boolean;
  onClose: () => void;
}

function statusColor(status: string, category?: 'productive' | 'away' | null): string {
  switch (status) {
    case 'ready':
    case 'talking':
      return 'bg-brand-green';
    case 'on_call':
      return 'bg-cyan-500';
    case 'wrap_up':
      return 'bg-purple-400';
    case 'break':
    case 'personal':
    case 'lunch':
      return 'bg-amber-400';
    case 'pc_it_issues':
      return 'bg-orange-500';
    case 'meeting':
    case 'special_projects':
    case 'supervisor_help':
    case 'training':
      return 'bg-primary';
    default:
      // Tenant-defined reasons (#1517): no hardcoded case, so fall back to
      // the reason's category from /api/kpi/live.
      if (category === 'productive') return 'bg-brand-green';
      if (category === 'away') return 'bg-amber-400';
      return 'bg-muted-foreground/30';
  }
}

function formatMins(mins: number): string {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
}

/**
 * Live-ticking time-in-status. Seeds from the server's `since` timestamp
 * (epoch-ms) and re-computes every 30s so supervisors don't have to
 * manually refresh the drawer to see the duration advance. Falls back
 * to `fallbackMinutes` (the server's `status_duration_minutes` snapshot)
 * when `since` is missing, which happens for migrated sessions that
 * pre-date presence tracking.
 */
function StatusElapsed({
  since,
  fallbackMinutes,
}: {
  since: number | undefined;
  fallbackMinutes: number;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!since) return;
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, [since]);
  const mins = since ? Math.max(0, Math.floor((now - since) / 60000)) : fallbackMinutes;
  return <>{formatMins(mins)}</>;
}

/**
 * Display name with fallbacks. Some SSO-provisioned reps have no
 * first/last populated, in which case the server already falls back to
 * the email local-part. This layer is defensive — if the server ever
 * returns an empty string we still show something usable rather than
 * a blank row.
 */
function displayName(rep: Rep): string {
  if (rep.name && rep.name.trim()) return rep.name.trim();
  if (rep.email) return rep.email.split('@')[0] || rep.email;
  return 'Unknown';
}

/** Two-letter initials derived from the display name, or "?" when we've
 *  got literally nothing. Safe against empty/single-word names. */
function initials(rep: Rep): string {
  const name = displayName(rep);
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0]?.slice(0, 2) ?? '?').toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

export function TeamDrawer({ open, onClose }: TeamDrawerProps) {
  const [reps, setReps] = useState<Rep[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { subscribe } = useWebSocket();

  // Override modal state + reason catalog. Reasons are fetched per
  // override target (#1410) — role-gated reasons filter by the TARGET
  // rep's role, so the list can differ rep to rep.
  const canForce = hasPermission(PERMISSIONS['admin.rep_status.force_change']);
  // Supervisor+ surfaces inside this drawer: per-rep contact counts and the
  // "Forced by X" attribution badge. Reps see the presence list without them.
  const canViewTeamFull = hasPermission(PERMISSIONS['dashboard.view_team']);
  const [reasons, setReasons] = useState<StatusReason[] | null>(null);
  const [overrideTarget, setOverrideTarget] = useState<Rep | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // scope=tenant: presence list is not agency-scoped — reps need to see
      // teammates and supervisors across the tenant, not just their own agency.
      const data = await apiGet<{ clocked_in: Rep[]; summary: { clocked_in_count: number } }>(
        '/api/kpi/live?scope=tenant',
      );
      setReps(data.clocked_in || []);
    } catch {
      setError('Unable to load team data');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Load the target-filtered reason list when the override modal opens.
  // for_user makes the server filter by the TARGET's role (#1410).
  useEffect(() => {
    if (!overrideTarget) return;
    setReasons(null);
    apiGet<{ reasons: StatusReason[] }>(
      `/api/kpi/status-reasons?for_user=${encodeURIComponent(overrideTarget.rep_id)}`,
    )
      .then((data) => setReasons(data.reasons ?? []))
      .catch(() => setReasons([]));
  }, [overrideTarget]);

  // Live updates via the kpi WS channel. The presence publisher fires on
  // clock-in/out, status change, call start/end, and the heartbeat-sweep
  // offline event (see src/cache/presence.ts). The subscribe helper debounces
  // bursts so a flurry of status changes still only triggers one reload.
  // Replaces the previous 30s setInterval poll.
  useEffect(() => {
    if (!open) return;
    return subscribeToKpiLiveEvents(subscribe, load);
  }, [open, subscribe, load]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <div className="fixed inset-0 bg-black/20 z-[9998]" onClick={onClose} />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="fixed top-0 right-0 w-[320px] h-full bg-card border-l border-border/60 shadow-[-4px_0_20px_rgba(0,0,0,0.1)] z-[9999] flex flex-col"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-border/60 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <h3 className="text-[0.875rem] font-semibold text-foreground">Team</h3>
                <span className="px-2 py-0.5 rounded-full text-[0.625rem] font-bold bg-brand-green/10 text-brand-green border border-brand-green/20">
                  {reps.length} online
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={load}
                  className="p-1.5 rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors cursor-pointer"
                >
                  <RefreshCw className="w-3.5 h-3.5 stroke-[1.5]" />
                </button>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Rep list */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-5 text-center text-muted-foreground text-[0.8125rem]">
                  Loading...
                </div>
              ) : error ? (
                <div className="p-5 text-center text-destructive text-[0.8125rem]">{error}</div>
              ) : reps.length === 0 ? (
                <div className="p-5 text-center text-muted-foreground text-[0.8125rem]">
                  No one is clocked in
                </div>
              ) : (
                reps.map((rep) => {
                  const isSystemState =
                    rep.current_status === 'on_call' || rep.current_status === 'wrap_up';
                  return (
                    <div
                      key={rep.rep_id}
                      className="flex items-center gap-3 px-5 py-3 border-b border-border/40"
                    >
                      <div className="relative">
                        <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[0.75rem] font-bold">
                          {initials(rep)}
                        </div>
                        <span
                          className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-card ${statusColor(rep.current_status, rep.status_category)}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <div
                            className="text-[0.8125rem] font-medium text-foreground truncate"
                            title={rep.email}
                          >
                            {displayName(rep)}
                          </div>
                          {canViewTeamFull && rep.forced_by_name && (
                            <span
                              title={`Forced by ${rep.forced_by_name}${rep.forced_note ? ` — ${rep.forced_note}` : ''}`}
                              className="inline-flex items-center gap-0.5 text-[0.5625rem] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 border border-amber-500/20 shrink-0"
                            >
                              <AlertCircle className="w-2.5 h-2.5 stroke-[2.5]" /> Forced
                            </span>
                          )}
                        </div>
                        <div className="text-[0.6875rem] text-muted-foreground flex items-center gap-2">
                          <span className="capitalize">
                            {rep.current_status.replace(/_/g, ' ')}
                          </span>
                          <span>·</span>
                          <StatusElapsed
                            since={rep.status_changed_at}
                            fallbackMinutes={rep.status_duration_minutes}
                          />
                        </div>
                      </div>
                      {canViewTeamFull && (
                        <div className="text-right flex-shrink-0">
                          <div className="text-[0.75rem] font-medium text-foreground">
                            {rep.contacts_this_session}
                          </div>
                          <div className="text-[0.625rem] text-muted-foreground">contacts</div>
                        </div>
                      )}
                      {canForce && (
                        <button
                          onClick={() => setOverrideTarget(rep)}
                          disabled={isSystemState}
                          title={
                            isSystemState
                              ? 'Cannot override while rep is on a call or wrapping up — clock them out if needed'
                              : 'Override status or clock out'
                          }
                          className={`text-[0.625rem] font-semibold uppercase tracking-wider px-2 py-1 rounded-md border shrink-0 transition-colors ${
                            isSystemState
                              ? 'border-border/40 text-muted-foreground/60 cursor-not-allowed'
                              : 'border-border/60 text-muted-foreground hover:bg-secondary hover:text-foreground cursor-pointer'
                          }`}
                        >
                          Override
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>

          {overrideTarget && (
            <OverrideStatusModal
              rep={overrideTarget}
              reasons={reasons ?? []}
              onClose={() => setOverrideTarget(null)}
              onDone={() => {
                setOverrideTarget(null);
                void load();
              }}
            />
          )}
        </>
      )}
    </AnimatePresence>
  );
}

function OverrideStatusModal({
  rep,
  reasons,
  onClose,
  onDone,
}: {
  rep: Rep;
  reasons: StatusReason[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [status, setStatus] = useState<string>('ready');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmingClockOut, setConfirmingClockOut] = useState(false);
  const [formError, setFormError] = useState('');

  // Group reasons by category for the select — matches the Header picker's
  // visual grouping so the mental model is consistent across the two
  // surfaces a supervisor might set a status from.
  const productive = reasons.filter((r) => r.category === 'productive');
  const away = reasons.filter((r) => r.category === 'away');
  const other = reasons.filter((r) => r.category !== 'productive' && r.category !== 'away');

  const handleSubmitStatus = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (note.length > 500) {
      setFormError('Note cannot exceed 500 characters');
      return;
    }

    setSubmitting(true);
    try {
      await apiPost(`/api/admin/rep-status/${rep.rep_id}/force`, {
        status,
        note: note.trim() || null,
      });
      toast.success(`${rep.name || rep.email} status updated`);
      onDone();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Status override failed');
    }
    setSubmitting(false);
  };

  const handleClockOut = async () => {
    setFormError('');

    if (note.length > 500) {
      setFormError('Note cannot exceed 500 characters');
      return;
    }

    setSubmitting(true);
    try {
      await apiPost(`/api/admin/rep-status/${rep.rep_id}/clock-out`, {
        note: note.trim() || null,
      });
      toast.success(`${rep.name || rep.email} clocked out`);
      onDone();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Clock-out failed');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <form
        onSubmit={handleSubmitStatus}
        className="relative bg-card border border-border/60 rounded-2xl shadow-2xl w-full max-w-md p-6 z-10"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-[0.9375rem] font-semibold">Override status</h3>
            <p className="text-[0.6875rem] text-muted-foreground mt-0.5">
              Change {rep.name || rep.email}&apos;s status, or clock them out
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-secondary transition-colors cursor-pointer"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {formError && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-destructive/5 border border-destructive/20 text-destructive text-[0.75rem]">
            {formError}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-[0.6875rem] font-bold text-muted-foreground uppercase tracking-wider mb-1.5">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 text-[0.8125rem] bg-secondary/50 border border-border/60 rounded-xl focus:border-primary/40 focus:ring-2 focus:ring-primary/10 outline-none transition-all"
            >
              <option value="ready">Ready</option>
              {productive.length > 0 && (
                <optgroup label="Productive">
                  {productive.map((r) => (
                    <option key={r.code} value={r.code}>
                      {r.label}
                    </option>
                  ))}
                </optgroup>
              )}
              {away.length > 0 && (
                <optgroup label="Away">
                  {away.map((r) => (
                    <option key={r.code} value={r.code}>
                      {r.label}
                    </option>
                  ))}
                </optgroup>
              )}
              {other.length > 0 && (
                <optgroup label="Other">
                  {other.map((r) => (
                    <option key={r.code} value={r.code}>
                      {r.label}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          <div>
            <label className="block text-[0.6875rem] font-bold text-muted-foreground uppercase tracking-wider mb-1.5">
              Note <span className="opacity-60">(optional, shown to rep + audit log)</span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="e.g. Pulling you off break — a priority contact just came in"
              className="w-full px-3 py-2 text-[0.8125rem] bg-secondary/50 border border-border/60 rounded-xl focus:border-primary/40 focus:ring-2 focus:ring-primary/10 outline-none transition-all resize-none"
            />
            <div className="text-[0.625rem] text-muted-foreground text-right mt-1">
              {note.length}/500
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center gap-2 mt-5">
          {confirmingClockOut ? (
            <div className="flex items-center gap-2">
              <span className="text-[0.6875rem] text-muted-foreground">Clock out?</span>
              <button
                type="button"
                onClick={handleClockOut}
                disabled={submitting}
                className="px-3 py-1.5 text-[0.6875rem] font-semibold rounded-lg bg-destructive text-white hover:bg-destructive/90 cursor-pointer disabled:opacity-50"
              >
                Yes, clock out
              </button>
              <button
                type="button"
                onClick={() => setConfirmingClockOut(false)}
                disabled={submitting}
                className="px-3 py-1.5 text-[0.6875rem] font-medium rounded-lg border border-border/60 hover:bg-secondary cursor-pointer disabled:opacity-50"
              >
                Back
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingClockOut(true)}
              disabled={submitting}
              className="px-3 py-2 text-[0.75rem] font-medium rounded-lg border border-destructive/30 text-destructive hover:bg-destructive/5 transition-colors cursor-pointer disabled:opacity-50"
            >
              Clock out
            </button>
          )}

          {!confirmingClockOut && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-[0.75rem] font-medium rounded-lg border border-border/60 hover:bg-secondary transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-[0.75rem] font-semibold rounded-lg bg-primary text-white hover:bg-primary/90 shadow-sm cursor-pointer disabled:opacity-50"
              >
                {submitting ? 'Setting…' : 'Set status'}
              </button>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
